import React, { useContext, useEffect, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { Badge, Box, Flex, Spinner } from "theme-ui";

import { getMyApiKeys, createApiKey, updateApiKey, revokeApiKey, reactivateApiKey } from "api/ApiKeys";
import { trackActivity } from "api/ActivityTracker";

import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import BasicModal from "components/ui/BasicModal";
import Span from "components/typography/Span";
import TableData from "components/ui/TableData";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import { useToast } from "components/toast";
import { CheckMarkIcon, KeyIcon, QuestionMarkIcon, WarningIcon } from "components/svg";
import H3 from "components/typography/H3";
import Separator from "components/ui/Separator";
import UserContext from "context/User";
import P from "components/typography/P";
import EllipsisIcon from "components/svg/icons/EllipsisIcon";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import { ModalIcon, ModalTitleWrapper, ModalTitle, ModalSubtitle, EllipsisWrapper } from '../styled';
import { themevals } from "theme/themevals";



export default function ApiSettings({ clientCompany }: { clientCompany: any }) {
  const toast = useToast();
  const { user } = useContext<any>(UserContext);

  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshData, setRefreshData] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editingKey, setEditingKey] = useState<any>(null);
  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
  const [limitModal, setLimitModal] = useState<any>(false);
  const [viewModal, setViewModal] = useState(false);
  const [viewingKey, setViewingKey] = useState<any>(null);

  const MAX_ACTIVE_API_KEYS = 3;

  const columnHelper = createColumnHelper();

  const formatDate = (dateString: string): string => {
    return moment(dateString).format("MMM D, YYYY");
  };

  const environmentOptions = [
    { value: "development", label: "Development" },
    { value: "production", label: "Production" },
  ];

  const openEditModal = (apiKey: any) => {
    setEditingKey(apiKey);
    setEditModal(true);
  };

  const closeEditModal = () => {
    setEditingKey(null);
    setEditModal(false);
  };

  const openViewModal = (apiKey: any) => {
    setViewingKey(apiKey);
    setViewModal(true);
  };

  const closeViewModal = () => {
    setViewingKey(null);
    setViewModal(false);
  };

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      cell: (info: any) => (
        <Span color="#000" fontWeight="500">{info.getValue()}</Span>
      ),
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor("isActive", {
      id: "status",
      cell: (info: any) => (
        info.getValue() 
          ? <Badge as="span" variant="success">Active</Badge>
          : <Badge as="span" variant="danger">Revoked</Badge>
      ),
      header: () => <span>Status</span>,
      meta: {
        style: { width: "120px" },
      },
    }),
    columnHelper.accessor("allowedDomain", {
      id: "allowedDomain",
      cell: (info: any) => (
        <Span color="#818181">{info.getValue() || "-"}</Span>
      ),
      header: () => <span>Domain</span>,
    }),
    columnHelper.accessor("environment", {
      id: "environment",
      cell: (info: any) => (
        <Span color="#818181" style={{ textTransform: "capitalize" }}>{info.getValue()}</Span>
      ),
      header: () => <span>Environment</span>,
      meta: {
        style: { width: "150px" },
      },
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      cell: (info: any) => (
        <Span color="#818181">{formatDate(info.getValue())}</Span>
      ),
      header: () => <span>Date Created</span>,
      meta: {
        style: { width: "150px" },
      },
    }),
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        const row = info.row.original;
        const menuItems = [
          { label: "Edit", onClick: () => openEditModal(row) },
          { label: "View", onClick: () => openViewModal(row) },
          ...(row.isActive
            ? [
                {
                  label: "Revoke",
                  style: { color: themevals.colors.danger },
                  onClick: () => showRevokeConfirmation(row),
                },
              ]
            : [
                {
                  label: "Reactivate",
                  style: { color: themevals.colors.success },
                  onClick: () => showReactivateConfirmation(row),
                },
              ]),
        ];

        return (
          <EllipsisWrapper>
            <DropDownMenu
              toggleComponent={
                <EllipsisIcon fill={"#818181"} width="20px" height="20px" />
              }
              items={menuItems}
            />
          </EllipsisWrapper>
        );
      },
      header: () => <span></span>,
      meta: {
        style: { width: "50px" },
      },
    }),
  ];

  const CreateApiKeySchema = Yup.object().shape({
    name: Yup.string().required("Name is required."),
    environment: Yup.string().oneOf(["production", "development"]).required("Environment is required."),
    allowedDomain: Yup.string(),
    description: Yup.string(),
  });

  const EditApiKeySchema = Yup.object().shape({
    name: Yup.string().required("Name is required."),
    allowedDomain: Yup.string(),
    description: Yup.string(),
  });

  const loadApiKeys = async () => {
    setLoading(true);
    const res = await getMyApiKeys();
    setApiKeys(res?.items || []);
    setLoading(false);
  };

  const submitCreateApiKey = async (values: any, { setErrors, resetForm }: any) => {
    const create = {
      name: values.name,
      environment: values.environment,
      allowedDomain: values.allowedDomain || null,
      description: values.description || null,
    };

    const res = await createApiKey({ create });
    if (res?.id) {
      toast.add("API Key created.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "client_company_settings",
        action: "create_api_key",
        actionType: "create",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "api_key",
        entityId: res?.id ?? null,
        metadata: {
          name: create.name,
          environment: create.environment,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      setCreateModal(false);
      resetForm();
      loadApiKeys();
    } else {
      toast.add(res?.message ? res.message : "Error creating API Key", "var(--theme-ui-colors-danger)");
    }
  };

  const submitEditApiKey = async (values: any, { setErrors }: any) => {
    const update = {
      name: values.name,
      allowedDomain: values.allowedDomain || null,
      description: values.description || null,
    };

    const res = await updateApiKey({ id: editingKey.id, update });
    if (res?.id) {
      toast.add("API key edited.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "client_company_settings",
        action: "update_api_key",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "api_key",
        entityId: res?.id ?? null,
        metadata: {
          name: update.name,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      closeEditModal();
      loadApiKeys();
    } else {
      toast.add(res?.message ? res.message : "Error updating API Key", "var(--theme-ui-colors-danger)");
    }
  };

  const confirmRevoke = async (apiKey: any) => {
    setConfirmSubmit(true);
    const res = await revokeApiKey({ id: apiKey.id });
    if (res?.id) {
      toast.add("API key revoked.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "client_company_settings",
        action: "revoke_api_key",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "api_key",
        entityId: res?.id ?? null,
        metadata: {
          name: apiKey.name,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      loadApiKeys();
    } else {
      toast.add(res?.message ? res.message : "Error revoking API Key", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
    setConfirmModal(false);
  };

  const confirmReactivate = async (apiKey: any) => {
    setConfirmSubmit(true);
    const res = await reactivateApiKey({ id: apiKey.id });
    if (res?.id) {
      toast.add("API key reactivated.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "client_company_settings",
        action: "reactivate_api_key",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "api_key",
        entityId: res?.id ?? null,
        metadata: {
          name: apiKey.name,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      loadApiKeys();
    } else {
      toast.add(res?.message ? res.message : "Error reactivating API Key", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
    setConfirmModal(false);
  };

  const showRevokeConfirmation = (apiKey: any, fromEditModal = false) => {
    setConfirmModal(
      <BasicModal
        title="Revoke API Key?"
        size="sm"
        cancel_click={() => {
          setConfirmModal(false);
        }}
        cancel_text="No, cancel"
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
          <BasicButton
            type="button"
            scheme="danger"
            onClick={() => {
              if (fromEditModal) {
                closeEditModal();
              }
              confirmRevoke(apiKey);
            }}
            $submitting={confirmSubmit}
            $iconHeight="14px"
            $loadingColor="#fff"
            keepText={true}
          >
            Revoke
          </BasicButton>
        }
      >
        <P textAlign="center" marginTop={0} marginBottom={0}>
          Are you sure you want to revoke this API key?
        </P>
      </BasicModal>
    );
  };

  const handleRevokeFromModal = () => {
    if (!editingKey) return;
    showRevokeConfirmation(editingKey, true);
  };

  const getActiveApiKeysCount = () => {
    return apiKeys.filter((key) => key.isActive).length;
  };

  const showLimitReachedModal = () => {
    setLimitModal(
      <BasicModal
        title="Limit Reached!"
        size="sm"
        cancel_click={() => setLimitModal(false)}
        cancel_text="Close"
        icon={<WarningIcon fill={"#818181"} height="30px" />}
      >
        <P textAlign="center" marginTop={0} marginBottom={0} lineHeight="22px">
          You may only have {MAX_ACTIVE_API_KEYS} Active API keys at one time. Revoke one of your active keys to create a new one.
        </P>
      </BasicModal>
    );
  };

  const showReactivateConfirmation = (apiKey: any, fromEditModal = false) => {
    // Check if we've reached the limit
    if (getActiveApiKeysCount() >= MAX_ACTIVE_API_KEYS) {
      if (fromEditModal) {
        closeEditModal();
      }
      showLimitReachedModal();
      return;
    }
    setConfirmModal(
      <BasicModal
        title="Reactivate API Key?"
        size="sm"
        cancel_click={() => {
          setConfirmModal(false);
        }}
        cancel_text="No, cancel"
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
          <BasicButton
            type="button"
            scheme="success"
            onClick={() => {
              if (fromEditModal) {
                closeEditModal();
              }
              confirmReactivate(apiKey);
            }}
            $submitting={confirmSubmit}
            $iconHeight="14px"
            $loadingColor="#fff"
            keepText={true}
          >
            Reactivate
          </BasicButton>
        }
      >
        <P textAlign="center" marginTop={0} marginBottom={0}>
          Are you sure you want to reactivate this API key?
        </P>
      </BasicModal>
    );
  };

  const handleReactivateFromModal = () => {
    if (!editingKey) return;
    showReactivateConfirmation(editingKey, true);
  };

  const toggleCreateModal = () => {
    if (!createModal && getActiveApiKeysCount() >= MAX_ACTIVE_API_KEYS) {
      showLimitReachedModal();
      return;
    }
    setCreateModal(!createModal);
  };

  useEffect(() => {
    loadApiKeys();
  }, []);

  return (
    <>
      <Box sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <H3 marginTop={"10px"} marginBottom={0} fontSize={"16px"} fontWeight={"600"} fontFamily={"Poppins"}>
          API
        </H3>
      </Box>
      <Separator />

      <Box sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        {loading ? (
          <Box sx={{ textAlign: "center", padding: "40px" }}>
            <Spinner size={25} strokeWidth={3} title="loading" />
          </Box>
        ) : apiKeys.length === 0 ? (
          /* Empty State */
          <Flex sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <P color="#818181" fontSize="14px" fontFamily="Poppins" marginTop={0} marginBottom={0}>
              There are currently no API keys associated with your company.
            </P>
            <BasicButton
              scheme="secondaryOutline"
              onClick={toggleCreateModal}
            >
              Create API Key
            </BasicButton>
          </Flex>
        ) : (
          /* Table with API Keys */
          <TableData
            title={""}
            api={getMyApiKeys}
            columns={columns}
            refresh={refreshData}
            enableSearch={false}
            enablePaging={false}
            actions={[
              <BasicButton
                key="create-api-key"
                scheme="secondaryOutline"
                onClick={toggleCreateModal}
              >
                Create API Key
              </BasicButton>,
            ]}
          />
        )}
      </Box>

      {/* Create API Key Modal */}
      <Modal isOpen={createModal} toggle={toggleCreateModal} centered={true} size="sm">
        <Formik
          initialValues={{
            name: "",
            environment: "development",
            allowedDomain: "",
            description: "",
          }}
          onSubmit={submitCreateApiKey}
          validationSchema={CreateApiKeySchema}
        >
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => (
            <Form noValidate autoComplete="off">
              <ModalHeader toggle={toggleCreateModal}>
                <ModalIcon>
                  <KeyIcon fill={"#000"} width="20px" />
                </ModalIcon>
                <ModalTitleWrapper>
                  <ModalTitle>Create API Key</ModalTitle>
                  <ModalSubtitle>Create a new API key for widget integration.</ModalSubtitle>
                </ModalTitleWrapper>
              </ModalHeader>
              <ModalBody>
                <TextInput
                  name="name"
                  label="Name"
                  value={values.name}
                  onChange={(val: any) => setFieldValue("name", val)}
                  autoComplete="off"
                  $errors={errors.name && submitCount > 0 ? errors.name : null}
                />
                <SelectInput
                  name="environment"
                  label="Environment"
                  value={values.environment}
                  onChange={(val: any) => setFieldValue("environment", val)}
                  options={environmentOptions}
                  $errors={errors.environment && submitCount > 0 ? errors.environment : null}
                  $customStyles={{ marginBottom: "30px" }}
                />
                <TextInput
                  name="allowedDomain"
                  label="Allowed Domain"
                  value={values.allowedDomain}
                  onChange={(val: any) => setFieldValue("allowedDomain", val)}
                  autoComplete="off"
                  placeholder="e.g. example.com"
                />
                <TextInput
                  type="textarea"
                  name="description"
                  label="Description"
                  value={values.description}
                  onChange={(val: any) => setFieldValue("description", val)}
                  autoComplete="off"
                  $customStyles={{ marginBottom: "30px" }}
                />
              </ModalBody>
              <ModalFooter>
                <BasicButton
                  outline={true}
                  styles={{ margin: ".25rem 10px .25rem 0" }}
                  type="button"
                  onClick={toggleCreateModal}
                  scheme="secondary"
                >
                  Cancel
                </BasicButton>
                <BasicButton
                  styles={{ margin: ".25rem 0 .25rem 0" }}
                  type="submit"
                  $submitting={isSubmitting}
                  $iconHeight="14px"
                  $loadingColor="#fff"
                  keepText={true}
                >
                  {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                  Create Key
                </BasicButton>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* View API Key Modal */}
      <Modal isOpen={viewModal} toggle={closeViewModal} centered={true} size="sm">
        <ModalHeader toggle={closeViewModal}>
          <ModalIcon>
            <KeyIcon fill={"#000"} width="20px" />
          </ModalIcon>
          <ModalTitleWrapper>
            <ModalTitle>View API Key</ModalTitle>
            <ModalSubtitle>Copy your API key for authorized use of the API</ModalSubtitle>
          </ModalTitleWrapper>
        </ModalHeader>
        <ModalBody>
          <TextInput
            name="apiKey"
            label="Key"
            value={viewingKey?.apiKey || ""}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onChange={() => {}}
            readonly={true}
            autoComplete="off"
          />
          <P color="#818181" fontSize="12px" fontFamily="Poppins" marginTop="20px" marginBottom={"20px"} lineHeight="18px">
            {viewingKey?.environment === "development"
              ? "This is a development key. Use it with the sandbox API at https://api-sandbox-jscrm.getmulch.com. Requests made with this key will not affect production data."
              : `This is a production key. Use it with the API at ${process.env.REACT_APP_BACKEND || 'https://api-jscrm.getmulch.com'}. Requests made with this key will affect live data.`}
          </P>
        </ModalBody>
        <ModalFooter>
          <BasicButton
            outline={true}
            type="button"
            onClick={closeViewModal}
            scheme="secondary"
          >
            Close
          </BasicButton>
        </ModalFooter>
      </Modal>

      {/* Edit API Key Modal */}
      <Modal isOpen={editModal} toggle={closeEditModal} centered={true} size="sm">
        <Formik
          initialValues={{
            name: editingKey?.name || "",
            environment: editingKey?.environment || "development",
            allowedDomain: editingKey?.allowedDomain || "",
            description: editingKey?.description || "",
          }}
          onSubmit={submitEditApiKey}
          validationSchema={EditApiKeySchema}
          enableReinitialize
        >
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => (
            <Form noValidate autoComplete="off">
              <ModalHeader toggle={closeEditModal}>
                <ModalIcon>
                  <KeyIcon fill={"#000"} width="20px" />
                </ModalIcon>
                <ModalTitleWrapper>
                  <ModalTitle>Edit API Key</ModalTitle>
                  <ModalSubtitle>Edit an API key for authorized use of the API</ModalSubtitle>
                </ModalTitleWrapper>
              </ModalHeader>
              <ModalBody>
                <TextInput
                  name="name"
                  label="Name"
                  value={values.name}
                  onChange={(val: any) => setFieldValue("name", val)}
                  autoComplete="off"
                  $errors={errors.name && submitCount > 0 ? errors.name : null}
                />
                <SelectInput
                  name="environment"
                  label="Environment"
                  value={values.environment}
                  onChange={(val: any) => setFieldValue("environment", val)}
                  options={environmentOptions}
                  readonly={true}
                  $customStyles={{ marginBottom: "30px" }}
                />
                <TextInput
                  name="allowedDomain"
                  label="Domain"
                  value={values.allowedDomain}
                  onChange={(val: any) => setFieldValue("allowedDomain", val)}
                  autoComplete="off"
                  placeholder="e.g. example.com"
                />
                <TextInput
                  type="textarea"
                  name="description"
                  label="Description"
                  value={values.description}
                  onChange={(val: any) => setFieldValue("description", val)}
                  autoComplete="off"
                  $customStyles={{ marginBottom: "30px" }}
                />
              </ModalBody>
              <ModalFooter>
                <Flex sx={{ display: "flex", flexGrow: 1, justifyContent: "space-between" }}>
                  {editingKey?.isActive ? (
                    <BasicButton
                      scheme="dangerSecondary"
                      styles={{ margin: ".25rem 0 .25rem 0" }}
                      type="button"
                      onClick={handleRevokeFromModal}
                    >
                      Revoke
                    </BasicButton>
                  ) : (
                    <BasicButton
                      scheme="success"
                      styles={{ margin: ".25rem 0 .25rem 0" }}
                      type="button"
                      onClick={handleReactivateFromModal}
                    >
                      Reactivate
                    </BasicButton>
                  )}
                  <Flex>
                    <BasicButton
                      outline={true}
                      styles={{ margin: ".25rem 10px .25rem 0" }}
                      type="button"
                      onClick={closeEditModal}
                      scheme="secondary"
                    >
                      Cancel
                    </BasicButton>
                    <BasicButton
                      styles={{ margin: ".25rem 0 .25rem 0" }}
                      type="submit"
                      $submitting={isSubmitting}
                      $iconHeight="14px"
                      $loadingColor="#fff"
                      keepText={true}
                    >
                      {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                      Save
                    </BasicButton>
                  </Flex>
                </Flex>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Revoke Confirmation Modal */}
      {confirmModal}

      {/* Limit Reached Modal */}
      {limitModal}
    </>
  );
}
